import type * as GeoJSON from "geojson";
import { FireSimulation } from "../../types/FireSimulation";

/**
 * One selectable ignition point for animation-mode partition picking.
 * `partition` is stored exactly as the simulation results provide it.
 */
export interface StoryIgnitionPoint {
  partition: string;
  lat: number;
  lng: number;
  /** 1-based display index ("Ignition N"). */
  label: number;
}

/**
 * Flatten a simulation's results[] into unique ignition points. Ensembles
 * replicate the same base points once per member (stacked at one location), so
 * points are deduped by location keeping the FIRST instance's partition — the
 * same collapse RiskFromLayer performs for its ignition squares.
 */
export function extractIgnitionPoints(
  results: FireSimulation[] | undefined | null
): StoryIgnitionPoint[] {
  if (!results?.length) return [];
  const byLocation = new Map<string, StoryIgnitionPoint>();
  results.forEach((result) => {
    (result.ignitions ?? []).forEach((ignition) => {
      const lat = ignition.start_point_lat;
      const lng = ignition.start_point_lng;
      if (lat == null || lng == null || ignition.partition == null) return;
      const key = `${lat},${lng}`;
      if (!byLocation.has(key)) {
        byLocation.set(key, {
          partition: ignition.partition,
          lat,
          lng,
          label: byLocation.size + 1,
        });
      }
    });
  });
  return Array.from(byLocation.values());
}

/** GeoJSON for the clickable ignition markers (property `partition` rides along). */
export function ignitionPointsToGeoJson(
  points: StoryIgnitionPoint[],
  selectedPartition: string | number | null
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point.lng, point.lat] },
      properties: {
        partition: point.partition,
        label: point.label,
        isSelected:
          selectedPartition != null &&
          String(point.partition) === String(selectedPartition),
      },
    })),
  };
}
